/**
 * The words and the colours of the result.
 *
 * The page is a toy, and this file is where it is allowed to be one. Everything that reads the
 * model still lives in `detector/`, and this file never changes a number.
 *
 * `detector/interpret.ts` stays a faithful port of the Python wording. It supplies the share of a
 * bin, and `sideNote` puts that share into a lighter sentence. The number is the number.
 */

import { binOf } from './detector/interpret';
import type { DetectorMeta } from './detector/types';

/** One band of the scale, from meatbag to robot. */
export interface Verdict {
  /** The line in large type. */
  label: string;
  /** The picture beside it. */
  emoji: string;
  /** The CSS suffix, which chooses the colour. */
  tone: 'meat' | 'meat-ish' | 'unsure' | 'bot-ish' | 'bot';
  /** One line under the label. */
  aside: string;
}

/**
 * Name the band that a score falls in.
 *
 * The two ends are the confident ones, and the middle band says so plainly. Four passages in ten
 * land between 0.2 and 0.8, where the tool knows very little, so that band must never read as an
 * answer.
 */
export function verdictOf(score: number): Verdict {
  if (score < 0.2) {
    return {
      label: 'Written by a meatbag',
      emoji: '🧠',
      tone: 'meat',
      aside: 'This has fingerprints all over it.',
    };
  }
  if (score < 0.4) {
    return {
      label: 'Probably a meatbag',
      emoji: '🙂',
      tone: 'meat-ish',
      aside: 'Leaning human, but not betting the house on it.',
    };
  }
  if (score < 0.6) {
    return {
      label: 'No idea, honestly',
      emoji: '🤷',
      tone: 'unsure',
      aside: 'Dead centre. This is the machine shrugging at you.',
    };
  }
  if (score < 0.8) {
    return {
      label: 'Probably a robot',
      emoji: '👀',
      tone: 'bot-ish',
      aside: 'Something about this is a bit too tidy.',
    };
  }
  return {
    label: 'Written by a robot',
    emoji: '🤖',
    tone: 'bot',
    aside: 'Beep boop. This one is not fooling anybody.',
  };
}

/**
 * Return the honest line that sits under the verdict, in lighter words.
 *
 * The share comes from `score_bins` in the artefact, which the export copies from the Python
 * table. An empty bin carries no share, so it carries no sentence.
 */
export function sideNote(score: number, meta: DetectorMeta): string {
  const bin = binOf(score, meta.score_bins);
  if (bin.count === 0 || bin.ai_share === null) {
    return 'No test passage ever scored here, so this is uncharted ground.';
  }

  const share = Math.round(bin.ai_share * 100);
  if (share >= 50) {
    return `Of the test passages that scored around here, ${share} in 100 really were robot-written.`;
  }
  return `Of the test passages that scored around here, ${100 - share} in 100 really were human.`;
}

/** Return the encouraging line for a text that is too short to score. */
export function tooShort(needed: number): string {
  return needed === 1
    ? 'One more letter and the machine will have a go.'
    : `Give it ${needed.toLocaleString()} more letters and it will have a go.`;
}
